import { _ } from "../../../../core/polyglot/polyglot.js";
import AbstractValueRow from "./AbstractValueRow.js";
import KeyFigureFormulaNode from "../../../keyFigure/formula/KeyFigureFormulaNode.js";
import keyFigureValueFormatter from "../../../keyFigure/rendering/keyFigureValueFormatter.js";
import NegatedFormulaNode from "../../../keyFigure/formula/NegatedFormulaNode.js";

/**
 * Key figure row.
 *
 * @param {keyFigure} spec.keyFigure - Key figure of the row.
 * @param {string} spec.style - Style used to display the row.
 * @param {boolean} [spec.negated=false] - Whether the row's value
 * should be the opposite of the corresponding kpi value.
 */
class KeyFigureRow extends AbstractValueRow {
	constructor({ keyFigure, style, negated }) {
		super(...arguments);
		this._keyFigure = keyFigure;
		this._style = style;
		this._negated = negated;
	}

	isNegated() {
		return this._negated;
	}

	setNegated(value) {
		this._negated = value;
	}

	getKeyFigure() {
		return this._keyFigure;
	}

	getStyle() {
		return this._style;
	}

	getTitle() {
		if (this.isValid()) {
			return this._createTitleWithAccountOperationShortName(
				this._keyFigure.getName(),
			);
		}
		return _("Unknown key figure");
	}

	getFormatter({ defaultFormatter, column }) {
		if (
			column.hasPercentageFormat() ||
			this._keyFigure.getValueType() === "Amount"
		) {
			return defaultFormatter;
		}
		return keyFigureValueFormatter({
			currencyFormatter: defaultFormatter,
		}).formatterFor(this._keyFigure);
	}

	isValid() {
		return !!this._keyFigure;
	}

	accept(visitor) {
		return visitor.visitKeyFigureRow(this);
	}

	_asBaseFormulaNode() {
		const node = new KeyFigureFormulaNode({ keyFigure: this._keyFigure });

		if (this.isNegated()) {
			return new NegatedFormulaNode({ expression: node });
		}
		return node;
	}

	_serializedProperties() {
		const properties = super._serializedProperties(...arguments);

		properties.keyFigureId = this._keyFigure
			? this._keyFigure.getId()
			: "UnknownKeyFigure";

		properties.style = this.getStyle();

		properties.negated = this._negated;

		return properties;
	}

	static getType() {
		return "KeyFigureRow";
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.keyFigure = context.keyFigures[data.keyFigureId];
	}
}

export default KeyFigureRow;
